import React, { useState, useEffect } from 'react';
import { Modal, Card, Typography, Input, Button, message } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  SoundOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import styles from '@/assets/styles/WordDetail.module.less';
import classNames from 'classnames';
import { getWordAudio } from '@/libs/api';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface WordDataType {
  word: string;
  phonetic?: string;
  translations: Array<{
    pos?: string;
    meanings: string[];
  }>;
  example?: string;
  dictId?: number;
  aiTranslation?: string;
  aiTranslationId?: number;
}

interface VocabularyWord {
  id: number;
  word: string;
  wordData: WordDataType;
  translation: string | null;
  note: string | null;
  addedAt: string;
  vocabularyListId: number;
}

interface WordDetailProps {
  visible: boolean;
  onClose: () => void;
  currentWord: VocabularyWord | null;
  onPrevWord?: () => void;
  onNextWord?: () => void;
  onUpdateNote?: (wordId: number, note: string) => Promise<void>;
}

const WordDetail: React.FC<WordDetailProps> = ({
  visible,
  onClose,
  currentWord,
  onPrevWord,
  onNextWord,
  onUpdateNote,
}) => {
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  useEffect(() => {
    if (currentWord) {
      setNoteContent(currentWord.note || '');
    }
  }, [currentWord]);

  const handleSaveNote = async () => {
    if (!currentWord || !onUpdateNote) return;

    try {
      await onUpdateNote(currentWord.id, noteContent);
      setIsEditingNote(false);
      message.success('笔记已保存');
    } catch (error) {
      message.error('保存笔记失败');
      console.error('Save note error:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && onPrevWord) {
      onPrevWord();
    } else if (e.key === 'ArrowRight' && onNextWord) {
      onNextWord();
    }
  };

  if (!currentWord) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      styles={{
        mask: {
          background: 'rgba(0, 0, 0, 0.45)',
        },
      }}
      className={styles.wordDetailModal}
    >
      <div onKeyDown={handleKeyDown} tabIndex={0}>
        {onPrevWord && (
          <Button
            className={classNames(styles.navigationButton, styles.prev)}
            type="text"
            icon={<LeftOutlined />}
            onClick={onPrevWord}
          />
        )}

        <Card className={styles.wordCard}>
          <div className={styles.wordContent}>
            {/* 单词标题区 */}
            <div className={styles.titleArea}>
              <div className={styles.mainTitle}>
                <Title level={2}>{currentWord.word}</Title>
                {currentWord.wordData.phonetic && (
                  <span className={styles.phonetic}>/{currentWord.wordData.phonetic}/</span>
                )}
              </div>
              <Button
                type="text"
                icon={<SoundOutlined />}
                onClick={async () => {
                  try {
                    await getWordAudio(currentWord.word);
                  } catch (error) {
                    message.error('发音失败');
                    console.error('Get word audio error:', error);
                  }
                }}
              />
            </div>

            {/* 词义列表 */}
            <div className={styles.translations}>
              {Array.isArray(currentWord.wordData.translations) &&
                currentWord.wordData.translations.map((trans, index) => (
                  <div key={index}>
                    {trans.pos && <span className={styles.pos}>{trans.pos}</span>}
                    <ul>
                      {trans.meanings.map((meaning, mIndex) => (
                        <li key={mIndex}>{meaning}</li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>

            {/* 例句 */}
            {currentWord.wordData.example && (
              <div className={styles.example}>
                <span className={styles.label}>例句</span>
                <div className={styles.content}>{currentWord.wordData.example}</div>
              </div>
            )}

            {/* 笔记区域 */}
            <div className={styles.noteArea}>
              <div className={styles.noteHeader}>
                <span className={styles.label}>笔记</span>
                {!isEditingNote ? (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditingNote(true)}
                  />
                ) : (
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveNote}>
                    保存
                  </Button>
                )}
              </div>
              {isEditingNote ? (
                <TextArea
                  value={noteContent}
                  onChange={e => setNoteContent(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  placeholder="添加笔记..."
                />
              ) : (
                <Paragraph>{noteContent || '暂无笔记'}</Paragraph>
              )}
            </div>
          </div>
        </Card>

        {onNextWord && (
          <Button
            className={classNames(styles.navigationButton, styles.next)}
            type="text"
            icon={<RightOutlined />}
            onClick={onNextWord}
          />
        )}
      </div>
    </Modal>
  );
};

export default WordDetail;
